import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from '../hooks/use-toast';
import { Clock, Trash2, Users, LogIn, LogOut, AlertTriangle, KeyRound, Search, RotateCcw, Check, X, ScanFace, Settings2, Save, MapPin, ShieldCheck } from 'lucide-react';
import AttendanceMatrix from '../components/clock/AttendanceMatrix';
import DailyDigest from '../components/clock/DailyDigest';
import PresentTodayPanel from '../components/clock/PresentTodayPanel';
import { ShiftReminders } from '../components/clock/ShiftReminders';
import ReportsTab from '../components/clock/ReportsTab';
import StationsTab from '../components/clock/StationsTab';
import EventsTab from '../components/clock/EventsTab';
import FlaggedTab from '../components/clock/FlaggedTab';
import IpReviewTab from '../components/clock/IpReviewTab';

const API = process.env.REACT_APP_BACKEND_URL;

const TZ = 'Asia/Dubai';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      timeZone: TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: TZ,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(',', '');
  } catch { return iso; }
};

const ClockAdmin = () => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('dashboard'); // dashboard | stations | pins | events | reports | attendance
  const [branches, setBranches] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flaggedCount, setFlaggedCount] = useState(0);

  // Staff PINs
  const [pinRows, setPinRows] = useState([]);
  const [pinSearch, setPinSearch] = useState('');
  const [pinStatusFilter, setPinStatusFilter] = useState('active'); // active | inactive | all
  const [pinDialog, setPinDialog] = useState(null); // selected employee row
  const [pinValue, setPinValue] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [bulkPinLoading, setBulkPinLoading] = useState(false);

  // Manual clock-out dialog (shared across dashboard / events / reports tabs)
  const [clockOutTarget, setClockOutTarget] = useState(null);   // { event_id, employee_id, employee_name, employee_code, in_at }
  const [clockOutMode, setClockOutMode] = useState('now');       // 'now' | 'custom'
  const [clockOutCustom, setClockOutCustom] = useState('');      // datetime-local string
  const [clockOutNote, setClockOutNote] = useState('');
  const [clockOutBusy, setClockOutBusy] = useState(false);

  const loadBranches = useCallback(() =>
    axios.get(`${API}/api/clock/branches`).then(r => setBranches(r.data || [])), []);
  const loadDashboard = useCallback(() =>
    axios.get(`${API}/api/clock/dashboard`, { headers }).then(r => setDashboard(r.data))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);
  const loadFlaggedCount = useCallback(() =>
    axios.get(`${API}/api/clock/flagged`, { headers })
      .then(r => setFlaggedCount(r.data?.unreviewed || 0))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadBranches(), loadDashboard()]).finally(() => setLoading(false));
    loadFlaggedCount();
    const t = setInterval(() => { loadDashboard(); loadFlaggedCount(); }, 30000);
    return () => clearInterval(t);
  }, [loadBranches, loadDashboard, loadFlaggedCount]);

  const loadPins = useCallback(() =>
    axios.get(`${API}/api/clock/admin/pins`, { headers }).then(r => setPinRows(r.data || []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);

  useEffect(() => { if (tab === 'pins') loadPins(); }, [tab, loadPins]);

  // ----- Clock-out rules settings -----
  const [rules, setRules] = useState(null);
  const [rulesSaving, setRulesSaving] = useState(false);
  const loadRules = useCallback(() =>
    axios.get(`${API}/api/clock/admin/clockout-settings`, { headers })
      .then(r => setRules(r.data.current))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);
  useEffect(() => { if (tab === 'rules' && !rules) loadRules(); }, [tab, rules, loadRules]);

  const saveRules = async () => {
    setRulesSaving(true);
    try {
      const res = await axios.put(`${API}/api/clock/admin/clockout-settings`, {
        alert_hours: Number(rules.alert_hours),
        auto_close_enabled: !!rules.auto_close_enabled,
        auto_close_hours: Number(rules.auto_close_hours),
        credited_hours: Number(rules.credited_hours),
        present_min_hours: Number(rules.present_min_hours),
        nudge_enabled: !!rules.nudge_enabled,
        nudge_hours: Number(rules.nudge_hours),
        nudge2_enabled: !!rules.nudge2_enabled,
        nudge2_hours: Number(rules.nudge2_hours),
        exempt_employee_codes: rules.exempt_employee_codes || [],
      }, { headers });
      setRules(res.data.current);
      toast({ title: 'Clock-out rules saved', description: 'New rules apply from the next 15-minute check.' });
    } catch (e) {
      toast({ title: 'Failed to save', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setRulesSaving(false); }
  };

  // ----- Geofence exemptions -----
  const [geoExempt, setGeoExempt] = useState(null);
  const [geoSearch, setGeoSearch] = useState('');
  const loadGeoExempt = useCallback(() =>
    axios.get(`${API}/api/clock/admin/geo-exempt`, { headers })
      .then(r => setGeoExempt(r.data.rows || []))
      .catch(() => setGeoExempt([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);
  useEffect(() => { if (tab === 'rules' && geoExempt === null) loadGeoExempt(); }, [tab, geoExempt, loadGeoExempt]);

  const toggleGeoExempt = async (row, next) => {
    setGeoExempt(list => list.map(r => r.employee_id === row.employee_id ? { ...r, exclude_geofence: next } : r));
    try {
      await axios.post(`${API}/api/clock/admin/geo-exempt`, { employee_id: row.employee_id, exclude_geofence: next }, { headers });
      toast({ title: next ? `${row.name} exempted from geofencing` : `${row.name} back under geofencing` });
    } catch {
      setGeoExempt(list => list.map(r => r.employee_id === row.employee_id ? { ...r, exclude_geofence: !next } : r));
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  // ----- Clock exemptions (always-in-report + bypass Wi-Fi/IP & geofence) -----
  const [clockExemptSearch, setClockExemptSearch] = useState('');
  const toggleClockExempt = async (row, next) => {
    const code = (row.employee_code || '').toUpperCase();
    const current = (rules?.exempt_employee_codes || []).map(c => String(c).toUpperCase());
    const updated = next ? Array.from(new Set([...current, code])) : current.filter(c => c !== code);
    const prev = rules?.exempt_employee_codes || [];
    setRules(r => ({ ...r, exempt_employee_codes: updated }));
    try {
      const res = await axios.put(`${API}/api/clock/admin/clockout-settings`, { exempt_employee_codes: updated }, { headers });
      setRules(res.data.current);
      toast({ title: next ? `${row.name} is now clock-exempt` : `${row.name} removed from clock exemptions`,
        description: next ? 'Always shown in the report + can clock in/out on any network.' : undefined });
    } catch (e) {
      setRules(r => ({ ...r, exempt_employee_codes: prev }));
      toast({ title: 'Failed to update exemption', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const openPinDialog = (row) => {
    setPinDialog(row);
    const digits = (row.employee_code || '').replace(/\D/g, '');
    setPinValue(digits.length >= 4 ? digits.slice(-4) : '');
  };

  const savePin = async () => {
    if (!pinDialog) return;
    if (pinValue && !/^\d{4,6}$/.test(pinValue)) {
      toast({ title: 'Invalid PIN', description: 'PIN must be 4 to 6 digits', variant: 'destructive' });
      return;
    }
    setPinLoading(true);
    try {
      const res = await axios.post(`${API}/api/clock/admin/set-pin`, {
        employee_id: pinDialog.id,
        pin: pinValue || null,
      }, { headers });
      toast({ title: 'PIN updated', description: res.data.message || `PIN set to ${res.data.pin}` });
      setPinDialog(null);
      setPinValue('');
      loadPins();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setPinLoading(false); }
  };

  const quickResetPin = async (row) => {
    if (!window.confirm(`Reset PIN for ${row.name}?\nA new PIN will be auto-assigned (last 4 of Employee Code, or 1234).`)) return;
    try {
      const res = await axios.post(`${API}/api/clock/admin/set-pin`, { employee_id: row.id, pin: null }, { headers });
      toast({ title: 'PIN reset', description: `${row.name}: PIN is now ${res.data.pin}` });
      loadPins();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const clearPin = async (row) => {
    if (!window.confirm(`Clear PIN for ${row.name}?\nThey will need to set a new PIN via the portal before clocking in.`)) return;
    try {
      await axios.delete(`${API}/api/clock/admin/pin/${row.id}`, { headers });
      toast({ title: 'PIN cleared' });
      loadPins();
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const activateDefaultPinsForAll = async (overwrite = false) => {
    const msg = overwrite
      ? `Reset every active staff member's PIN to the default (last 4 of Employee Code, or 1234)?\n\nThis will OVERWRITE existing PINs.`
      : `Activate the default PIN (last 4 of Employee Code, or 1234) for every active staff member who doesn't have a PIN yet?`;
    if (!window.confirm(msg)) return;
    setBulkPinLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/clock/admin/bulk-set-default-pins?overwrite=${overwrite ? 'true' : 'false'}`,
        {},
        { headers }
      );
      toast({ title: 'Default PINs activated', description: res.data.message });
      loadPins();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setBulkPinLoading(false);
    }
  };

  const filteredPinRows = pinRows.filter(r => {
    if (pinStatusFilter === 'active' && !r.effectively_active) return false;
    if (pinStatusFilter === 'inactive' && r.effectively_active) return false;
    const q = pinSearch.trim().toLowerCase();
    if (!q) return true;
    return (r.name || '').toLowerCase().includes(q)
      || (r.employee_code || '').toLowerCase().includes(q)
      || (r.department || '').toLowerCase().includes(q);
  });

  // ----- Manual clock-out -----
  const openClockOutDialog = (target) => {
    setClockOutTarget(target);
    setClockOutMode('now');
    setClockOutCustom('');
    setClockOutNote('');
  };
  const submitClockOut = async () => {
    if (!clockOutTarget?.event_id) return;
    let out_timestamp = null;
    if (clockOutMode === 'custom') {
      if (!clockOutCustom) {
        toast({ title: 'Please pick a clock-out time', variant: 'destructive' });
        return;
      }
      // Treat the picker value as UAE local time (UTC+4) → ISO UTC
      const localMs = new Date(clockOutCustom).getTime();
      out_timestamp = new Date(localMs - 4 * 60 * 60 * 1000).toISOString();
    }
    setClockOutBusy(true);
    try {
      await axios.post(
        `${API}/api/clock/admin/close-missed/${clockOutTarget.event_id}`,
        { out_timestamp, note: clockOutNote || '' },
        { headers },
      );
      toast({ title: `Clocked out ${clockOutTarget.employee_name}` });
      setClockOutTarget(null);
      loadDashboard();
      // Events / Reports tabs subscribe to their own data and will pick up the change on re-render.
    } catch (e) {
      toast({ title: 'Clock-out failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setClockOutBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500" data-testid="clock-admin-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="clock-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Clock className="h-7 w-7 text-violet-600" /> Clock In / Out
          </h1>
          <p className="text-sm text-gray-500">Live attendance tracking via QR & NFC — auto-refreshes every 30s</p>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { k: 'dashboard', l: 'Live' },
          { k: 'stations', l: 'Stations' },
          { k: 'pins', l: 'Staff PINs' },
          { k: 'events', l: 'Events' },
          { k: 'flagged', l: 'Flagged' },
          { k: 'ip-review', l: 'IP Review' },
          { k: 'reports', l: 'Reports' },
          { k: 'attendance', l: 'Attendance' },
          { k: 'rules', l: 'Rules' },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md ${tab === t.k ? 'bg-white shadow-sm' : 'text-gray-600'}`}
            data-testid={`clock-tab-${t.k}`}>
            {t.l}
            {t.k === 'flagged' && flaggedCount > 0 && (
              <span
                className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold align-middle"
                data-testid="clock-tab-flagged-badge"
              >
                {flaggedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Dashboard */}
      {tab === 'dashboard' && dashboard && (
        <div className="space-y-4">
          <PresentTodayPanel />
          <ShiftReminders />
          <DailyDigest />
          {dashboard.face_stats && (() => {
            const fs = dashboard.face_stats;
            const rate = fs.rate;
            const tone = rate === null ? 'gray' : rate >= 90 ? 'emerald' : rate >= 75 ? 'amber' : 'rose';
            const toneMap = {
              emerald: { card: 'from-emerald-50 to-teal-50 border-emerald-200', text: 'text-emerald-800', label: 'text-emerald-700', bar: 'bg-emerald-500' },
              amber: { card: 'from-amber-50 to-orange-50 border-amber-200', text: 'text-amber-800', label: 'text-amber-700', bar: 'bg-amber-500' },
              rose: { card: 'from-rose-50 to-pink-50 border-rose-200', text: 'text-rose-800', label: 'text-rose-700', bar: 'bg-rose-500' },
              gray: { card: 'from-gray-50 to-slate-50 border-gray-200', text: 'text-gray-700', label: 'text-gray-600', bar: 'bg-gray-400' },
            };
            const c = toneMap[tone];
            const problemStations = (fs.by_station || []).filter(s => s.rate !== null && s.rate < 90 && s.attempted >= 2);
            return (
              <Card className={`bg-gradient-to-br ${c.card}`} data-testid="face-match-rate-card">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className={`text-xs uppercase font-semibold mb-1 flex items-center gap-1 ${c.label}`}>
                        <ScanFace className="h-3.5 w-3.5" /> Face Match Rate · Today
                      </p>
                      <div className="flex items-end gap-2">
                        <p className={`text-4xl font-bold ${c.text}`} data-testid="face-match-rate">
                          {rate === null ? '—' : `${rate}%`}
                        </p>
                        <p className="text-xs text-gray-500 mb-1.5">
                          {fs.attempted === 0 ? 'No verifiable punches yet' : `${fs.verified}/${fs.attempted} auto-verified`}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4 text-center">
                      <div data-testid="face-stat-unverified">
                        <p className="text-lg font-bold text-amber-700">{fs.unverified}</p>
                        <p className="text-[10px] uppercase text-gray-500">Couldn't verify</p>
                      </div>
                      <div data-testid="face-stat-mismatch">
                        <p className="text-lg font-bold text-rose-700">{fs.mismatch}</p>
                        <p className="text-[10px] uppercase text-gray-500">Mismatch</p>
                      </div>
                      <div data-testid="face-stat-noselfie">
                        <p className="text-lg font-bold text-gray-600">{fs.no_selfie}</p>
                        <p className="text-[10px] uppercase text-gray-500">No selfie</p>
                      </div>
                    </div>
                  </div>
                  {/* Progress bar */}
                  {rate !== null && (
                    <div className="mt-3 h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
                      <div className={`h-full ${c.bar} rounded-full transition-all`} style={{ width: `${rate}%` }} />
                    </div>
                  )}
                  {/* Problem stations hint */}
                  {problemStations.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="face-problem-stations">
                      <span className="text-[11px] text-gray-500 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 text-amber-500" /> Check camera/lighting at:
                      </span>
                      {problemStations.map(s => (
                        <span key={s.station} className="text-[11px] bg-white/70 rounded-full px-2 py-0.5 font-medium text-gray-700">
                          {s.station} · {s.rate}% ({s.verified}/{s.attempted})
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase text-emerald-700 font-semibold mb-1 flex items-center gap-1"><LogIn className="h-3.5 w-3.5" /> Clocked In Now</p>
              <p className="text-4xl font-bold text-emerald-800" data-testid="count-clocked-in">{dashboard.clocked_in_now.length}</p>
              <div className="mt-3 max-h-60 overflow-auto space-y-1">
                {dashboard.clocked_in_now.length === 0 ? <p className="text-xs text-gray-500 italic">Nobody yet</p> :
                  dashboard.clocked_in_now.map(e => (
                    <div key={e.id} className="flex items-center justify-between text-xs bg-white/60 rounded px-2 py-1">
                      <span className="font-medium">{e.name} <span className="text-gray-400">· {e.employee_code}</span></span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-emerald-700">{fmtTime(e.last_in)}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-1.5 text-[10px] text-sky-700 border-sky-200 hover:bg-sky-50"
                          onClick={() => openClockOutDialog({
                            event_id: e.event_id,
                            employee_id: e.employee_id || e.id,
                            employee_name: e.name,
                            employee_code: e.employee_code,
                            in_at: e.last_in,
                          })}
                          data-testid={`live-clockout-${e.employee_code}`}
                          title="Manually clock out"
                        >
                          <LogOut className="h-3 w-3 mr-0.5" /> Clock out
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase text-amber-700 font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Still Clocked In (Prev. day)</p>
              <p className="text-4xl font-bold text-amber-800" data-testid="count-warnings">{dashboard.warnings_still_clocked_in.length}</p>
              <div className="mt-3 max-h-60 overflow-auto space-y-1">
                {dashboard.warnings_still_clocked_in.length === 0 ? <p className="text-xs text-gray-500 italic">Clean</p> :
                  dashboard.warnings_still_clocked_in.map(e => (
                    <div key={e.id} className="flex items-center justify-between text-xs bg-white/60 rounded px-2 py-1">
                      <span className="font-medium">{e.name} <span className="text-gray-400">· {e.employee_code}</span></span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-amber-700">{fmtDateTime(e.last_in)}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-1.5 text-[10px] text-sky-700 border-sky-200 hover:bg-sky-50"
                          onClick={() => openClockOutDialog({
                            event_id: e.event_id,
                            employee_id: e.employee_id || e.id,
                            employee_name: e.name,
                            employee_code: e.employee_code,
                            in_at: e.last_in,
                          })}
                          data-testid={`warning-clockout-${e.employee_code}`}
                          title="Manually clock out"
                        >
                          <LogOut className="h-3 w-3 mr-0.5" /> Clock out
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase text-gray-700 font-semibold mb-1 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> No Punch Today</p>
              <p className="text-4xl font-bold text-gray-800" data-testid="count-no-punch">{dashboard.not_clocked_in_today.length}</p>
              <div className="mt-3 max-h-60 overflow-auto space-y-1">
                {dashboard.not_clocked_in_today.slice(0, 20).map(e => (
                  <div key={e.id} className="text-xs bg-white/60 rounded px-2 py-1 font-medium">
                    {e.name} <span className="text-gray-400">· {e.employee_code}</span>
                  </div>
                ))}
                {dashboard.not_clocked_in_today.length > 20 && (
                  <p className="text-[11px] text-gray-500">+ {dashboard.not_clocked_in_today.length - 20} more…</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      {/* Stations */}
      {tab === 'stations' && (
        <StationsTab branches={branches} reloadBranches={loadBranches} />
      )}

      {/* Staff PINs */}
      {tab === 'pins' && (
        <div className="space-y-3" data-testid="pins-tab">
          <Card>
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Search name, employee code, department…"
                  value={pinSearch}
                  onChange={e => setPinSearch(e.target.value)}
                  className="pl-9"
                  data-testid="pin-search"
                />
              </div>
              <div className="flex gap-1 bg-gray-100 rounded-md p-1">
                {[
                  { k: 'active', l: 'Active' },
                  { k: 'inactive', l: 'Inactive' },
                  { k: 'all', l: 'All' },
                ].map(f => (
                  <button
                    key={f.k}
                    onClick={() => setPinStatusFilter(f.k)}
                    className={`px-3 py-1 text-xs font-medium rounded ${pinStatusFilter === f.k ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                    data-testid={`pin-filter-${f.k}`}
                  >
                    {f.l}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 ml-auto">
                {filteredPinRows.filter(r => r.has_pin).length} of {filteredPinRows.length} shown have PIN · {pinRows.filter(r => !r.effectively_active).length} inactive total
              </p>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkPinLoading}
                onClick={() => activateDefaultPinsForAll(false)}
                title="Set the default PIN (last 4 of Employee Code, or 1234) for every active staff member who doesn't have one yet"
                data-testid="bulk-default-pins-btn"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <KeyRound className="h-4 w-4 mr-1.5" />
                {bulkPinLoading ? 'Activating…' : 'Activate Default PIN for All'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="text-left p-3">Employee</th>
                    <th className="text-left p-3">Code</th>
                    <th className="text-left p-3">Department</th>
                    <th className="text-left p-3">Employment</th>
                    <th className="text-left p-3">PIN Status</th>
                    <th className="text-left p-3">Last Updated</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPinRows.length === 0 ? (
                    <tr><td colSpan="7" className="p-6 text-center text-gray-500">No employees match this search</td></tr>
                  ) : filteredPinRows.map(r => (
                    <tr
                      key={r.id}
                      className={`border-t hover:bg-gray-50 ${r.effectively_active ? '' : 'bg-rose-50/60 text-rose-900'}`}
                      data-testid={`pin-row-${r.employee_code}`}
                    >
                      <td className="p-3 font-medium">
                        {r.name}
                        {!r.effectively_active && <span className="ml-1 text-[10px] uppercase tracking-wide text-rose-700 font-semibold">· blocked</span>}
                      </td>
                      <td className="p-3 font-mono text-xs">{r.employee_code}</td>
                      <td className="p-3 text-xs">{r.department || '—'}</td>
                      <td className="p-3 text-xs">
                        {r.effectively_active ? (
                          <Badge className="bg-emerald-100 text-emerald-700">{r.employment_status || 'Active'}</Badge>
                        ) : (
                          <span className="inline-flex flex-col gap-0.5">
                            <Badge className="bg-rose-100 text-rose-700 w-fit">{r.employment_status || 'Inactive'}</Badge>
                            {r.last_working_date && <span className="text-[10px] text-rose-600">LWD: {r.last_working_date}</span>}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {r.has_pin ? (
                          <Badge className={r.effectively_active ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                            {r.effectively_active ? <><Check className="h-3 w-3 mr-1" /> Set</> : <><AlertTriangle className="h-3 w-3 mr-1" /> Set but blocked</>}
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600"><X className="h-3 w-3 mr-1" /> Not set</Badge>
                        )}
                      </td>
                      <td className="p-3 text-xs text-gray-500">{r.pin_updated_at ? fmtDateTime(r.pin_updated_at) : '—'}</td>
                      <td className="p-3 text-right space-x-1">
                        {r.effectively_active ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openPinDialog(r)} data-testid={`set-pin-btn-${r.employee_code}`}>
                              <KeyRound className="h-3.5 w-3.5 mr-1" /> Set PIN
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => quickResetPin(r)} title="Auto-reset PIN" data-testid={`reset-pin-btn-${r.employee_code}`}>
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                            {r.has_pin && (
                              <Button size="sm" variant="ghost" onClick={() => clearPin(r)} className="text-rose-600" title="Clear PIN">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </>
                        ) : (
                          r.has_pin ? (
                            <Button size="sm" variant="destructive" onClick={() => clearPin(r)} data-testid={`revoke-pin-btn-${r.employee_code}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-1" /> Revoke PIN
                            </Button>
                          ) : (
                            <span className="text-[11px] text-gray-400 italic">Access revoked</span>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Events */}
      {tab === 'events' && (
        <EventsTab branches={branches} onClockOut={openClockOutDialog} />
      )}

      {/* Flagged punches (anti-spoofing review) */}
      {tab === 'flagged' && (
        <FlaggedTab />
      )}

      {tab === 'ip-review' && (
        <IpReviewTab />
      )}

      {/* Reports */}
      {tab === 'reports' && (
        <ReportsTab onClockOut={openClockOutDialog} />
      )}

      {/* Attendance tab */}
      {tab === 'attendance' && (
        <AttendanceMatrix />
      )}

      {/* Clock-out Rules tab */}
      {tab === 'rules' && (
        <div className="max-w-2xl space-y-4" data-testid="rules-tab">
          {/* Attendance rule */}
          <Card data-testid="attendance-rule-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-emerald-600" />
                <div>
                  <h2 className="text-lg font-semibold">Attendance Rule</h2>
                  <p className="text-xs text-gray-500">Controls when a day is auto-marked <b>Present (P)</b> on the Attendance sheet.</p>
                </div>
              </div>
              {!rules ? (
                <p className="text-sm text-gray-500 py-4 text-center">Loading…</p>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label className="font-medium">Mark Present after</Label>
                      <p className="text-xs text-gray-500 mt-0.5">A staff member gets <b>P</b> on any day they work at least this many hours (across all clock-in/out sessions, including night shifts that end after midnight).</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        type="number" min="0" max="24" step="0.5"
                        value={rules.present_min_hours}
                        onChange={e => setRules(r => ({ ...r, present_min_hours: e.target.value }))}
                        className="w-24 text-center"
                        data-testid="rules-present-min-hours"
                      />
                      <span className="text-sm text-gray-500">hours</span>
                    </div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
                    {Number(rules.present_min_hours) > 0 ? (
                      <>Staff are marked <b>P</b> once they log <b>{rules.present_min_hours}h</b> or more in a day. Days below this stay blank (HR can still set P manually).</>
                    ) : (
                      <><b>Any completed clock-in/out</b> marks the day <b>P</b> (no minimum hours). Set a number above to require a minimum.</>
                    )} Leave codes (AL/SL/PH…) and manual overrides are unaffected.
                  </div>
                  <div className="flex items-center justify-end">
                    <Button onClick={saveRules} disabled={rulesSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="attendance-rule-save-btn">
                      <Save className="h-4 w-4 mr-1.5" /> {rulesSaving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-5">
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-violet-600" />
                <div>
                  <h2 className="text-lg font-semibold">Auto Clock-Out Rules</h2>
                  <p className="text-xs text-gray-500">Control when the system alerts and auto-closes forgotten clock-ins.</p>
                </div>
              </div>

              {!rules ? (
                <p className="text-sm text-gray-500 py-6 text-center">Loading rules…</p>
              ) : (
                <>
                  {/* Alert hours */}
                  <div className="flex items-start justify-between gap-4 border-b pb-4">
                    <div>
                      <Label className="font-medium">Missed clock-out alert after</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Notify the employee (and admin) once a session stays open this long.</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        type="number" min="1" max="72" step="0.5"
                        value={rules.alert_hours}
                        onChange={e => setRules(r => ({ ...r, alert_hours: e.target.value }))}
                        className="w-24 text-center"
                        data-testid="rules-alert-hours"
                      />
                      <span className="text-sm text-gray-500">hours</span>
                    </div>
                  </div>

                  {/* Missed-punch nudge */}
                  <div className="flex items-center justify-between gap-4 border-b pb-4">
                    <div>
                      <Label className="font-medium">Early "forgot to clock out?" nudge</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Send the staff member a one-time reminder (portal + email) — copied to HR — this many hours after they clock in, if they're still clocked in. Clock-exempt and attendance-excluded staff are skipped.</p>
                    </div>
                    <Switch
                      checked={!!rules.nudge_enabled}
                      onCheckedChange={v => setRules(r => ({ ...r, nudge_enabled: v }))}
                      data-testid="rules-nudge-enabled"
                    />
                  </div>
                  {rules.nudge_enabled && (
                    <div className="flex items-start justify-between gap-4 border-b pb-4">
                      <div>
                        <Label className="font-medium">Send nudge after</Label>
                        <p className="text-xs text-gray-500 mt-0.5">Hours after clock-in before the reminder goes out (fires before the missed clock-out alert above).</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Input
                          type="number" min="1" max="24" step="0.5"
                          value={rules.nudge_hours}
                          onChange={e => setRules(r => ({ ...r, nudge_hours: e.target.value }))}
                          className="w-24 text-center"
                          data-testid="rules-nudge-hours"
                        />
                        <span className="text-sm text-gray-500">hours</span>
                      </div>
                    </div>
                  )}
                  {/* Second nudge */}
                  <div className="flex items-center justify-between gap-4 border-b pb-4">
                    <div>
                      <Label className="font-medium">"Did you forget to clock out?" nudge</Label>
                      <p className="text-xs text-gray-500 mt-0.5">A second one-time reminder (portal + email) — copied to HR — for staff still clocked in after this many hours. Clock-exempt and attendance-excluded staff are skipped.</p>
                    </div>
                    <Switch
                      checked={!!rules.nudge2_enabled}
                      onCheckedChange={v => setRules(r => ({ ...r, nudge2_enabled: v }))}
                      data-testid="rules-nudge2-enabled"
                    />
                  </div>
                  {rules.nudge2_enabled && (
                    <div className="flex items-start justify-between gap-4 border-b pb-4">
                      <div>
                        <Label className="font-medium">Send second nudge after</Label>
                        <p className="text-xs text-gray-500 mt-0.5">Hours after clock-in (default 9). Should be later than the early nudge.</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Input
                          type="number" min="1" max="24" step="0.5"
                          value={rules.nudge2_hours ?? 9}
                          onChange={e => setRules(r => ({ ...r, nudge2_hours: e.target.value }))}
                          className="w-24 text-center"
                          data-testid="rules-nudge2-hours"
                        />
                        <span className="text-sm text-gray-500">hours</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-4 border-b pb-4">
                    <div>
                      <Label className="font-medium">Enable automatic clock-out</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Turn OFF to review and close forgotten sessions manually on the Live tab instead.</p>
                    </div>
                    <Switch
                      checked={!!rules.auto_close_enabled}
                      onCheckedChange={v => setRules(r => ({ ...r, auto_close_enabled: v }))}
                      data-testid="rules-auto-enabled"
                    />
                  </div>

                  {/* Auto-close hours */}
                  <div className={`flex items-start justify-between gap-4 border-b pb-4 ${rules.auto_close_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                    <div>
                      <Label className="font-medium">Auto clock-out after</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Sessions open longer than this are closed automatically. Must be ≥ the alert time.</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        type="number" min="1" max="168" step="0.5"
                        value={rules.auto_close_hours}
                        onChange={e => setRules(r => ({ ...r, auto_close_hours: e.target.value }))}
                        className="w-24 text-center"
                        data-testid="rules-autoclose-hours"
                      />
                      <span className="text-sm text-gray-500">hours</span>
                    </div>
                  </div>

                  {/* Credited hours */}
                  <div className={`flex items-start justify-between gap-4 ${rules.auto_close_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                    <div>
                      <Label className="font-medium">Credit worked hours on auto clock-out</Label>
                      <p className="text-xs text-gray-500 mt-0.5">When auto-closing, stamp the OUT this many hours after the clock-in.</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        type="number" min="0" max="24" step="0.5"
                        value={rules.credited_hours}
                        onChange={e => setRules(r => ({ ...r, credited_hours: e.target.value }))}
                        className="w-24 text-center"
                        data-testid="rules-credited-hours"
                      />
                      <span className="text-sm text-gray-500">hours</span>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                    {rules.auto_close_enabled ? (
                      <>Forgotten sessions are alerted after <b>{rules.alert_hours}h</b> and auto clocked-out after <b>{rules.auto_close_hours}h</b>, crediting <b>{rules.credited_hours}h</b> of work. Checked every 15 minutes.</>
                    ) : (
                      <>Automatic clock-out is <b>OFF</b>. Staff are still alerted after <b>{rules.alert_hours}h</b>, but you must close open sessions yourself from the <b>Live</b> tab ("Still Clocked In").</>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-gray-400">
                      {rules.updated_at ? `Last updated by ${rules.updated_by || '—'} on ${fmtDateTime(rules.updated_at)}` : 'Using default rules'}
                    </p>
                    <Button onClick={saveRules} disabled={rulesSaving} className="bg-violet-600 hover:bg-violet-700" data-testid="rules-save-btn">
                      <Save className="h-4 w-4 mr-1.5" /> {rulesSaving ? 'Saving…' : 'Save Rules'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Geofence exemptions */}
          <Card data-testid="geo-exempt-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-teal-600" />
                <div>
                  <h2 className="text-lg font-semibold">Geofence Exemptions</h2>
                  <p className="text-xs text-gray-500">Exempt specific staff (e.g. field / remote workers) from GPS geofence enforcement. They can then clock in/out from anywhere.</p>
                </div>
                {geoExempt && (
                  <span className="ml-auto text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-1">
                    {geoExempt.filter(r => r.exclude_geofence).length} exempted
                  </span>
                )}
              </div>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={geoSearch} onChange={e => setGeoSearch(e.target.value)} placeholder="Search staff by name, code or department…" className="pl-9 h-9" data-testid="geo-exempt-search" />
              </div>
              {geoExempt === null ? (
                <p className="text-sm text-gray-500 py-4 text-center">Loading staff…</p>
              ) : (
                <div className="max-h-96 overflow-y-auto divide-y border rounded-lg">
                  {geoExempt
                    .filter(r => `${r.name} ${r.employee_code} ${r.department}`.toLowerCase().includes(geoSearch.trim().toLowerCase()))
                    .map(r => (
                      <div key={r.employee_id} className="flex items-center justify-between px-3 py-2.5" data-testid={`geo-exempt-row-${r.employee_code}`}>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{r.name}</p>
                          <p className="text-[11px] text-gray-400">{r.employee_code}{r.department ? ` · ${r.department}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {r.exclude_geofence && <span className="text-[10px] uppercase font-semibold text-teal-700">Exempt</span>}
                          <Switch
                            checked={!!r.exclude_geofence}
                            onCheckedChange={(v) => toggleGeoExempt(r, v)}
                            data-testid={`geo-exempt-toggle-${r.employee_code}`}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Clock exemptions (always in report + bypass Wi-Fi/IP & geofence) */}
          <Card data-testid="clock-exempt-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-indigo-600" />
                <div>
                  <h2 className="text-lg font-semibold">Clock Exemptions</h2>
                  <p className="text-xs text-gray-500">Exempt staff are <b>always shown in the Attendance report</b> with their clock-out (even manual / long closes), and can <b>clock in/out from any network</b> (mobile data) — bypassing Wi-Fi/IP and geofence checks. EMP0001 is exempt by default.</p>
                </div>
                {rules && (
                  <span className="ml-auto text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2.5 py-1">
                    {(rules.exempt_employee_codes || []).length} exempted
                  </span>
                )}
              </div>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={clockExemptSearch} onChange={e => setClockExemptSearch(e.target.value)} placeholder="Search staff by name, code or department…" className="pl-9 h-9" data-testid="clock-exempt-search" />
              </div>
              {(geoExempt === null || !rules) ? (
                <p className="text-sm text-gray-500 py-4 text-center">Loading staff…</p>
              ) : (
                <div className="max-h-96 overflow-y-auto divide-y border rounded-lg">
                  {geoExempt
                    .filter(r => `${r.name} ${r.employee_code} ${r.department}`.toLowerCase().includes(clockExemptSearch.trim().toLowerCase()))
                    .map(r => {
                      const isExempt = (rules.exempt_employee_codes || []).map(c => String(c).toUpperCase()).includes((r.employee_code || '').toUpperCase());
                      return (
                        <div key={r.employee_id} className="flex items-center justify-between px-3 py-2.5" data-testid={`clock-exempt-row-${r.employee_code}`}>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{r.name}</p>
                            <p className="text-[11px] text-gray-400">{r.employee_code}{r.department ? ` · ${r.department}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {isExempt && <span className="text-[10px] uppercase font-semibold text-indigo-700">Exempt</span>}
                            <Switch
                              checked={isExempt}
                              onCheckedChange={(v) => toggleClockExempt(r, v)}
                              data-testid={`clock-exempt-toggle-${r.employee_code}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Set / Reset PIN dialog */}
      <Dialog open={!!pinDialog} onOpenChange={(o) => { if (!o) { setPinDialog(null); setPinValue(''); } }}>
        <DialogContent data-testid="set-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-violet-600" />
              {pinDialog?.has_pin ? 'Reset' : 'Set'} PIN for {pinDialog?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-violet-50 rounded p-3 text-xs space-y-1">
              <p><span className="text-gray-500">Employee:</span> <b>{pinDialog?.name}</b></p>
              <p><span className="text-gray-500">Code:</span> <span className="font-mono">{pinDialog?.employee_code}</span></p>
              <p><span className="text-gray-500">Status:</span> {pinDialog?.has_pin ? 'PIN already set' : 'No PIN set'}</p>
            </div>
            <div>
              <Label>New PIN (4–6 digits)</Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                value={pinValue}
                onChange={e => setPinValue(e.target.value.replace(/\D/g, ''))}
                placeholder="e.g. 1234"
                className="font-mono text-center text-xl tracking-[0.4em]"
                data-testid="set-pin-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Leave blank to auto-assign last 4 digits of employee code (or 1234 if no digits).
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setPinDialog(null); setPinValue(''); }}>Cancel</Button>
              <Button onClick={savePin} disabled={pinLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="set-pin-confirm">
                {pinLoading ? 'Saving…' : (pinDialog?.has_pin ? 'Reset PIN' : 'Set PIN')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Clock-Out dialog */}
      <Dialog open={!!clockOutTarget} onOpenChange={(o) => { if (!o) setClockOutTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="clockout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5 text-sky-600" />
              Manual Clock Out
            </DialogTitle>
          </DialogHeader>
          {clockOutTarget && (
            <div className="space-y-4">
              <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm">
                <div className="font-semibold text-gray-800">
                  {clockOutTarget.employee_name}
                  <span className="text-xs text-gray-500 ml-1">· {clockOutTarget.employee_code}</span>
                </div>
                {clockOutTarget.in_at && (
                  <div className="text-xs text-gray-600 mt-1">
                    Clocked in at <span className="font-mono">{fmtDateTime(clockOutTarget.in_at)}</span>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">Clock-out time</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setClockOutMode('now')}
                    className={`px-3 py-2 rounded-md border text-sm ${clockOutMode === 'now' ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    data-testid="clockout-mode-now"
                  >
                    Now (current UAE time)
                  </button>
                  <button
                    type="button"
                    onClick={() => setClockOutMode('custom')}
                    className={`px-3 py-2 rounded-md border text-sm ${clockOutMode === 'custom' ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    data-testid="clockout-mode-custom"
                  >
                    Custom time
                  </button>
                </div>
                {clockOutMode === 'custom' && (
                  <div className="mt-2">
                    <Input
                      type="datetime-local"
                      value={clockOutCustom}
                      onChange={(e) => setClockOutCustom(e.target.value)}
                      data-testid="clockout-custom-input"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Picker is in Asia/Dubai (UTC+4).</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input
                  value={clockOutNote}
                  onChange={(e) => setClockOutNote(e.target.value)}
                  placeholder="e.g. Forgot to punch out, left at 17:30"
                  data-testid="clockout-note"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setClockOutTarget(null)} disabled={clockOutBusy}>Cancel</Button>
                <Button
                  className="bg-sky-600 hover:bg-sky-700"
                  onClick={submitClockOut}
                  disabled={clockOutBusy}
                  data-testid="clockout-submit"
                >
                  {clockOutBusy ? 'Clocking out…' : 'Clock out'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClockAdmin;
